import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pencil, Search, X, ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

import EditarResponsavelModal from "../../components/EditarResponsavelModal/EditarResponsavelModal";
import ImportarAlunos from "../../components/ImportarAlunos/ImportarAlunos";
import { useUsuariosInstituicao } from "../../hooks/useUsuariosInstituicao";
import {
  usePermissaoAlunos,
  usePermissaoResponsaveis,
} from "../../hooks/usePermissaoEventos";
import { getUsuarioLogadoAdaptado, idsIguais } from "../../utils/ids";
import {
  obterTurma,
  editarTurma,
  listarResponsaveis,
  listarAlunos,
  vincularResponsavel,
  desvincularResponsavel,
  editarCargoContatoResponsavelTurma,
  atualizarPermissoesResponsavel,
  vincularAluno,
  desvincularAluno,
  TipoTurma,
} from "../../api/turmas";
import { permissoesTravadas } from "./permissoesTravadas";
import "./EditarTurma.scss";

const MSG_SEM_PERMISSAO_ALUNOS = "Este usuário não pode efetuar essa ação.";

const defaultPerms = { eventos: true, responsaveis: false, alunos: true };

function splitNomeComplemento(nomeCompleto = "", complemento = "") {
  if (complemento) return { nome: nomeCompleto, complemento };
  const parts = String(nomeCompleto).split(" - ");
  if (parts.length >= 2) {
    return { nome: parts[0], complemento: parts.slice(1).join(" - ") };
  }
  return { nome: nomeCompleto, complemento: "" };
}

// tipo do backend chega como "Primario"/"Secundario" (string enum) ou número.
function tipoToRadio(t) {
  const s = String(t ?? "").toLowerCase();
  return s.includes("secund") || s === "2" ? "secundaria" : "primaria";
}
function radioToEnum(r) {
  return r === "secundaria" ? TipoTurma.Secundario : TipoTurma.Primario;
}

// VinculoResponsavelDto → shape do EditarResponsavelModal
function mapResponsavel(v) {
  return {
    userId: v.userId,
    nome: v.nome ?? "",
    cargo: v.cargo ?? "",
    contato: v.contato ?? "",
    permissoes: {
      eventos: !!v.podeCriarEditarEventos,
      responsaveis: !!v.podeGerenciarResponsaveis,
      alunos: !!v.podeEditarListaAlunos,
    },
    fixo: !!v.fixo,
    removivel: !v.fixo,
  };
}

export default function EditarTurma() {
  const navigate = useNavigate();
  const { turmaId: routeTurmaId } = useParams();
  const turmaId = routeTurmaId || null;

  const usuarioLogado = useMemo(() => getUsuarioLogadoAdaptado(), []);
  const isCoordenador =
    String(usuarioLogado?.tipo || "").toLowerCase() === "coordenador";

  // Permissões por turma. Cada seção bloqueia conforme sua flag. Coord sempre pode.
  const avisarBloqueio = () => {
    Swal.fire({
      title: "Ação não permitida",
      text: MSG_SEM_PERMISSAO_ALUNOS,
      icon: "info",
      confirmButtonText: "Entendi",
      confirmButtonColor: "#2E4A67",
    });
  };

  const { pode: podeEditarAlunos } = usePermissaoAlunos(turmaId);
  const alunosBloqueado = !podeEditarAlunos;
  const avisarBloqueioAlunos = avisarBloqueio;

  const { pode: podeGerenciarResponsaveis } = usePermissaoResponsaveis(turmaId);
  const responsaveisBloqueado = !podeGerenciarResponsaveis;
  const avisarBloqueioResponsaveis = avisarBloqueio;

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [nome, setNome] = useState("");
  const [complemento, setComplemento] = useState("");
  const [tipoTurma, setTipoTurma] = useState("primaria");
  const [instituicaoId, setInstituicaoId] = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);
  const [alunos, setAlunos] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("edit");
  const [editIndex, setEditIndex] = useState(null);

  const [buscaAluno, setBuscaAluno] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [alunoSelecionadoId, setAlunoSelecionadoId] = useState(null);
  const searchWrapRef = useRef(null);

  const { usuarios: baseUsuarios } = useUsuariosInstituicao(instituicaoId);

  const usuariosComUser = useMemo(
    () => baseUsuarios.map((u) => ({ ...u, user: u.user ?? String(u.id) })),
    [baseUsuarios],
  );

  // Busca de "adicionar responsável" só lista Responsável e Aluno.
  // Coordenadores já entram automáticos pela coordenação; coords de outras
  // coordenações não podem ser responsáveis avulsos.
  const usuariosParaResponsavel = useMemo(
    () =>
      usuariosComUser.filter((u) => {
        const t = String(u.tipo || "").toLowerCase();
        return t === "responsavel" || t === "aluno";
      }),
    [usuariosComUser],
  );

  const carregarVinculos = useCallback(async () => {
    if (!turmaId) return;
    const [resp, als] = await Promise.all([
      listarResponsaveis(turmaId),
      listarAlunos(turmaId),
    ]);
    const respList = (Array.isArray(resp) ? resp : []).map(mapResponsavel);
    // Coordenadores (fixo) primeiro
    respList.sort((a, b) => (a.fixo === b.fixo ? 0 : a.fixo ? -1 : 1));
    setResponsaveis(respList);
    setAlunos(
      (Array.isArray(als) ? als : []).map((a) => ({
        id: a.userId,
        nome: a.nome,
      })),
    );
  }, [turmaId]);

  const carregarTurma = useCallback(async () => {
    if (!turmaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const t = await obterTurma(turmaId);
      const { nome: n, complemento: c } = splitNomeComplemento(
        t.nome || "",
        t.complemento || "",
      );
      setNome(n);
      setComplemento(c);
      setTipoTurma(tipoToRadio(t.tipo));
      setInstituicaoId(t.instituicaoId || null);
      await carregarVinculos();
    } catch (e) {
      setErro(e);
    } finally {
      setLoading(false);
    }
  }, [turmaId, carregarVinculos]);

  useEffect(() => {
    carregarTurma();
  }, [carregarTurma]);

  // Atualização automática: recarrega nome/complemento/responsáveis/alunos da
  // turma quando algo muda (foco da janela dispara turmas:changed globalmente).
  useEffect(() => {
    const onChange = () => carregarTurma();
    window.addEventListener("turmas:changed", onChange);
    window.addEventListener("app:data-changed", onChange);
    return () => {
      window.removeEventListener("turmas:changed", onChange);
      window.removeEventListener("app:data-changed", onChange);
    };
  }, [carregarTurma]);

  // Trocar coordenação no menu lateral → volta pra lista (turmas são outras).
  useEffect(() => {
    const onCoord = () => navigate("/editar-turma-coordenador");
    window.addEventListener("coordenacaoAtual:changed", onCoord);
    return () =>
      window.removeEventListener("coordenacaoAtual:changed", onCoord);
  }, [navigate]);

  useEffect(() => {
    const onDown = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const candidatosBusca = useMemo(() => {
    const q = buscaAluno.trim().toLowerCase();
    if (!q) return [];
    const jaVinculados = new Set(alunos.map((a) => String(a.id)));
    return baseUsuarios
      .filter((u) => String(u.tipo || "").toLowerCase() === "aluno")
      .filter((u) => !jaVinculados.has(String(u.id)))
      .filter(
        (u) =>
          String(u.nome || "")
            .toLowerCase()
            .includes(q) ||
          String(u.user || "")
            .toLowerCase()
            .includes(q) ||
          String(u.email || "")
            .toLowerCase()
            .includes(q),
      )
      .slice(0, 8)
      .map((u) => ({ id: u.id, nome: u.nome, matricula: u.user }));
  }, [buscaAluno, baseUsuarios, alunos]);

  // ---- Turma info ----
  const handleSalvarTopo = useCallback(async () => {
    if (!turmaId) return;
    try {
      await editarTurma(turmaId, {
        id: turmaId,
        nome: nome.trim(),
        complemento: complemento.trim() || null,
        tipo: radioToEnum(tipoTurma),
      });
      window.dispatchEvent(new Event("turmas:changed"));
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Alterações salvas",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Erro ao salvar", text: e?.message });
    }
  }, [turmaId, nome, complemento, tipoTurma]);

  // ---- Responsáveis ----
  const openEditarResponsavel = (index) => {
    setModalMode("edit");
    setEditIndex(index);
    setModalOpen(true);
  };
  const openAdicionarResponsavel = () => {
    setModalMode("add");
    setEditIndex(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditIndex(null);
  };

  const handleSaveResponsavelFromModal = async (payload) => {
    try {
      if (modalMode === "edit" && editIndex !== null) {
        const atual = responsaveis[editIndex];
        // Cargo/contato editáveis para todos (inclusive coord fixo).
        await editarCargoContatoResponsavelTurma(turmaId, atual.userId, {
          cargo: payload.cargo || null,
          contato: payload.contato || null,
        });
        // Permissões: salva só para responsável comum que não seja você mesmo.
        // O backend recusa (403) alterar as próprias permissões e as de um
        // coordenador (vínculo fixo). Sem esta guarda, o cargo/contato acima já
        // foi salvo e o PUT seguinte estoura — a tela acusa "erro ao salvar"
        // com o dado gravado, e a mudança só aparece ao recarregar a página.
        if (!permissoesTravadas(atual, usuarioLogado) && payload.permissoes) {
          const perms = { ...defaultPerms, ...payload.permissoes };
          await atualizarPermissoesResponsavel(turmaId, atual.userId, {
            podeCriarEditarEventos: !!perms.eventos,
            podeEditarListaAlunos: !!perms.alunos,
            podeGerenciarResponsaveis: !!perms.responsaveis,
          });
        }
      } else {
        // Adicionar manual: resolve login → Guid real
        const u = usuariosComUser.find(
          (x) => String(x.user) === String(payload.userId),
        );
        const realId = u?.id ?? payload.userId;
        const perms = { ...defaultPerms, ...(payload.permissoes || {}) };
        await vincularResponsavel(turmaId, {
          turmaId,
          userId: realId,
          cargo: payload.cargo || null,
          contato: payload.contato || null,
          podeCriarEditarEventos: !!perms.eventos,
          podeEditarListaAlunos: !!perms.alunos,
          podeGerenciarResponsaveis: !!perms.responsaveis,
        });
      }
      await carregarVinculos();
      closeModal();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro ao salvar responsável",
        text: e?.message || "Tente novamente.",
      });
    }
  };

  const handleRemoveResponsavelFromModal = async () => {
    if (editIndex === null) return;
    const atual = responsaveis[editIndex];
    if (atual.fixo) {
      Swal.fire({
        icon: "warning",
        title: "Coordenador obrigatório",
        text: "Coordenadores da coordenação não podem ser removidos da turma.",
      });
      return;
    }
    try {
      await desvincularResponsavel(turmaId, atual.userId);
      await carregarVinculos();
      closeModal();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Erro ao remover", text: e?.message });
    }
  };

  // ---- Alunos ----
  const handleAdicionarAluno = async () => {
    if (!alunoSelecionadoId) {
      Swal.fire({ icon: "warning", title: "Selecione um aluno" });
      return;
    }
    try {
      await vincularAluno(turmaId, alunoSelecionadoId);
      await carregarVinculos();
      setBuscaAluno("");
      setAlunoSelecionadoId(null);
      setDropdownOpen(false);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro ao adicionar",
        text: e?.message,
      });
    }
  };

  const handleRemoverAluno = async (uid) => {
    try {
      await desvincularAluno(turmaId, uid);
      await carregarVinculos();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Erro ao remover", text: e?.message });
    }
  };

  const Voltar = () =>
    isCoordenador ? (
      <button
        type="button"
        className="btn-voltar-coordenador"
        onClick={() => navigate("/editar-turma-coordenador")}
      >
        <ArrowLeft size={16} strokeWidth={3} />
        <span>Voltar</span>
      </button>
    ) : null;

  if (!turmaId) {
    return (
      <div className="editar-turma-page">
        <Voltar />
        <div className="editar-turma-box">
          <header className="page-header">
            <h1>Editar turma</h1>
          </header>
          <p className="empty-text">Nenhuma turma selecionada.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="editar-turma-page">
        <div className="editar-turma-box">
          <div className="skeleton-title" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="editar-turma-page">
        <Voltar />
        <div className="editar-turma-box">
          <header className="page-header">
            <h1>Editar turma</h1>
          </header>
          <p className="empty-text">Turma não encontrada.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`editar-turma-page ${
        isCoordenador
          ? "editar-turma-page--coordenador"
          : "editar-turma-page--padrao"
      }`}
    >
      <Voltar />

      <section className="editar-turma-box editar-turma-box--info">
        <header className="page-header">
          <h1>Editar turma</h1>
        </header>
        <div className="bloco-header">
          <h2>Informações da Turma</h2>
        </div>

        <div className="grid-2">
          <div className="field">
            <label>Nome</label>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome da turma"
            />
          </div>
          <div className="field">
            <label>Complemento</label>
            <input
              value={complemento}
              onChange={(e) => setComplemento(e.target.value)}
              placeholder="Turma 8"
            />
          </div>
        </div>

        {isCoordenador && (
          <div className="tipo-area">
            <div className="tipo-label">Selecione o tipo</div>
            <div className="radio-group">
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipoTurma"
                  checked={tipoTurma === "primaria"}
                  onChange={() => setTipoTurma("primaria")}
                />
                <span>Primária</span>
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipoTurma"
                  checked={tipoTurma === "secundaria"}
                  onChange={() => setTipoTurma("secundaria")}
                />
                <span>Secundária</span>
              </label>
            </div>
          </div>
        )}

        {/* Responsável também pode salvar nome/complemento da turma */}
        <button
          type="button"
          className="btn-primary"
          style={{ marginTop: 8 }}
          onClick={handleSalvarTopo}
        >
          Salvar informações
        </button>
      </section>

      <section
        className={`editar-turma-box editar-turma-box--responsaveis ${
          responsaveisBloqueado ? "editar-turma-box--bloqueado" : ""
        }`}
      >
        {responsaveisBloqueado && (
          <div
            className="permissao-overlay"
            onClick={avisarBloqueioResponsaveis}
            role="button"
            tabIndex={0}
            aria-label={MSG_SEM_PERMISSAO_ALUNOS}
          />
        )}
        <div className="bloco-header">
          <h2>Responsáveis da turma</h2>
        </div>

        <div className="responsaveis">
          {responsaveis.length > 0 ? (
            responsaveis.map((r, idx) => {
              const cargoNorm = String(r.cargo || "").toLowerCase();
              const tipoClasse = r.fixo
                ? "responsavel-card--coordenador"
                : "responsavel-card--simples";
              const cargoClasse =
                cargoNorm === "professor"
                  ? "responsavel-card--professor"
                  : cargoNorm === "responsável" || cargoNorm === "responsavel"
                    ? "responsavel-card--responsavel"
                    : "";
              return (
                <div
                  className={`responsavel-card ${tipoClasse} ${cargoClasse}`}
                  key={`${r.userId}-${idx}`}
                >
                  <div className="responsavel-top">
                    <span className="chip">{r.nome || "Responsável"}</span>
                    <button
                      type="button"
                      className="btn-edit"
                      onClick={() => openEditarResponsavel(idx)}
                      aria-label="Editar responsável"
                      title="Editar"
                    >
                      <Pencil size={10} />
                      <span className="btn-edit__text">Editar</span>
                    </button>
                  </div>
                  <div className="grid-2 inner">
                    <div className="field">
                      <label>Cargo</label>
                      <input value={r.cargo || ""} readOnly />
                    </div>
                    <div className="field">
                      <label>Contato</label>
                      <input value={r.contato || ""} readOnly />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-small">Nenhum responsável adicionado.</div>
          )}
        </div>

        <button
          type="button"
          className="btn-primary wide responsavel-add-btn"
          onClick={openAdicionarResponsavel}
        >
          Adicionar responsável
        </button>
      </section>

      <section
        className={`editar-turma-box editar-turma-box--alunos ${
          alunosBloqueado ? "editar-turma-box--bloqueado" : ""
        }`}
      >
        {alunosBloqueado && (
          <div
            className="permissao-overlay"
            onClick={avisarBloqueioAlunos}
            role="button"
            tabIndex={0}
            aria-label={MSG_SEM_PERMISSAO_ALUNOS}
          />
        )}
        <div className="section-head">
          <h2>Lista de Alunos</h2>
          <ImportarAlunos
            usuarios={baseUsuarios}
            faculdadeId={instituicaoId}
            idsJaExistentes={alunos.map((a) => a.id)}
            buttonLabel="Importar lista"
            className="btn-importar"
            onImportConfirm={async (idsNovos) => {
              try {
                for (const id of idsNovos) {
                  if (alunos.some((a) => idsIguais(a.id, id))) continue;
                  await vincularAluno(turmaId, id);
                }
                await carregarVinculos();
              } catch (e) {
                Swal.fire({
                  icon: "error",
                  title: "Erro ao importar",
                  text: e?.message,
                });
              }
            }}
          />
        </div>

        <div className="subsection">
          <p className="sub-title">Adicione alunos</p>
          <div className="add-aluno-actions">
            <div className="search-wrap" ref={searchWrapRef}>
              <Search className="search-ico" />
              <input
                type="text"
                value={buscaAluno}
                onChange={(e) => {
                  setBuscaAluno(e.target.value);
                  setDropdownOpen(true);
                  setAlunoSelecionadoId(null);
                }}
                onFocus={() => {
                  if (buscaAluno.trim()) setDropdownOpen(true);
                }}
                placeholder="Pesquise nome, matrícula ou email..."
              />
              {dropdownOpen && candidatosBusca.length > 0 && (
                <div className="search-dropdown">
                  {candidatosBusca.map((aluno) => (
                    <button
                      type="button"
                      key={String(aluno.id)}
                      className={`dropdown-item ${
                        idsIguais(alunoSelecionadoId, aluno.id) ? "active" : ""
                      }`}
                      onClick={() => {
                        setBuscaAluno(
                          `${aluno.nome} - ${aluno.matricula || ""}`,
                        );
                        setAlunoSelecionadoId(aluno.id);
                        setDropdownOpen(false);
                      }}
                    >
                      <span className="d-name">{aluno.nome}</span>
                      <span className="d-sub">{aluno.matricula}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className={`btn-primary add-aluno-btn ${
                alunoSelecionadoId ? "is-active" : "is-muted"
              }`}
              onClick={handleAdicionarAluno}
              disabled={!alunoSelecionadoId}
            >
              Adicionar aluno
            </button>
          </div>
        </div>

        <button
          type="button"
          className="btn-ver-lista"
          onClick={() => navigate(`/turma/${turmaId}/alunos`)}
        >
          Ver lista completa de alunos
        </button>

        <div className="alunos-list">
          {alunos.length > 0 ? (
            alunos.map((aluno) => (
              <div className="aluno-row" key={String(aluno.id)}>
                <div className="aluno-info">
                  <span className="aluno-nome">{aluno.nome}</span>
                </div>
                <button
                  type="button"
                  className="btn-x"
                  onClick={() => handleRemoverAluno(aluno.id)}
                  aria-label={`Remover ${aluno.nome}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))
          ) : (
            <div className="empty-small">Nenhum aluno adicionado.</div>
          )}
        </div>
      </section>

      <EditarResponsavelModal
        usuarioLogado={usuarioLogado}
        open={modalOpen}
        onClose={closeModal}
        usuarios={usuariosParaResponsavel}
        initialValue={
          modalMode === "edit" && editIndex !== null
            ? responsaveis[editIndex]
            : null
        }
        onSave={handleSaveResponsavelFromModal}
        onRemove={
          modalMode === "edit" && responsaveis[editIndex]?.fixo === false
            ? handleRemoveResponsavelFromModal
            : null
        }
        bloquearResponsavel={
          modalMode === "edit" && responsaveis[editIndex]?.fixo === true
        }
        bloquearRemocao={
          modalMode === "edit" && responsaveis[editIndex]?.fixo === true
        }
      />
    </div>
  );
}
